import { Injectable } from '@angular/core';
import { parseFlattenToPairs } from 'src/Utility/xml_jsonbuilder';
import { AuditNameValue, ParsedAuditPayload } from './caseaudit.models';

const UNC_PATH_REGEX = /\\\\[^\s;,"']+/g;

@Injectable({ providedIn: 'root' })
export class CaseAuditPayloadParserService {
  looksLikeJson(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
  }

  /**
   * Split only on `;` that introduce another top-level `Key:` pair.
   * Keeps free-form values intact (e.g. Chilkat "Endian; 64-bit" inside StatusMessage).
   */
  private splitTopLevelSemicolonPairs(value: string): string[] {
    return value
      .split(/;\s*(?=[A-Za-z_][\w./-]*\s*:)/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  looksLikeKeyValue(value: string): boolean {
    if (!value || typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    if (!trimmed || this.looksLikeJson(trimmed)) {
      return false;
    }
    const segments = this.splitTopLevelSemicolonPairs(trimmed);
    if (segments.length === 0) {
      return false;
    }
    const pairCount = segments.filter((seg) => {
      const idx = seg.indexOf(':');
      if (idx <= 0) {
        return false;
      }
      const key = seg.slice(0, idx).trim();
      return /^[A-Za-z_][\w./-]*$/.test(key);
    }).length;
    return pairCount > 0 && pairCount >= Math.ceil(segments.length / 2);
  }

  parseSemicolonKeyValues(value: string): AuditNameValue[] {
    const pairs: AuditNameValue[] = [];
    if (!value) {
      return pairs;
    }
    const segments = this.splitTopLevelSemicolonPairs(value);
    for (const segment of segments) {
      const trimmed = segment.trim();
      if (!trimmed) {
        continue;
      }
      const idx = trimmed.indexOf(':');
      if (idx <= 0) {
        if (pairs.length > 0) {
          pairs[pairs.length - 1].value =
            `${pairs[pairs.length - 1].value}; ${trimmed}`.trim();
        } else {
          pairs.push({ name: 'Detail', value: trimmed });
        }
        continue;
      }
      const name = trimmed.slice(0, idx).trim();
      // Reject mid-log "keys" that aren't top-level identifiers
      if (!/^[A-Za-z_][\w./-]*$/.test(name)) {
        if (pairs.length > 0) {
          pairs[pairs.length - 1].value =
            `${pairs[pairs.length - 1].value}; ${trimmed}`.trim();
        } else {
          pairs.push({ name: 'Detail', value: trimmed });
        }
        continue;
      }
      const rawValue = trimmed.slice(idx + 1).trim();
      pairs.push({ name, value: rawValue });
    }
    return pairs;
  }

  /**
   * Case A: JSON → flatten to name/value (no tree / no raw string).
   * Case B: semicolon Key:Value → grid pairs; nested JSON values also flattened.
   * Case C: plain text.
   */
  async parsePayload(raw: string | null | undefined): Promise<ParsedAuditPayload> {
    const text = (raw ?? '').toString();
    if (!text.trim()) {
      return { kind: 'text', text: '', pairs: [] };
    }

    const trimmed = text.trim();

    if (this.looksLikeJson(trimmed)) {
      const pairs = await parseFlattenToPairs(trimmed);
      if (pairs.length > 0) {
        return { kind: 'json', pairs, text: trimmed };
      }
      return { kind: 'text', text: trimmed, pairs: [] };
    }

    // Semicolon key-values that embed JSON in a value (e.g. StatusMessage:{...})
    if (this.looksLikeKeyValue(trimmed)) {
      const basePairs = this.parseSemicolonKeyValues(trimmed);
      if (basePairs.length > 0) {
        const expanded: AuditNameValue[] = [];
        for (const pair of basePairs) {
          const valueStr =
            pair.value == null ? '' : String(pair.value).trim();
          if (this.looksLikeJson(valueStr)) {
            const nested = await parseFlattenToPairs(valueStr);
            if (nested.length > 0) {
              for (const n of nested) {
                expanded.push({
                  name: `${pair.name}_${n.name}`,
                  value: n.value,
                });
              }
              continue;
            }
          }
          expanded.push(pair);
        }
        return { kind: 'keyvalue', pairs: expanded, text: trimmed };
      }
    }

    return { kind: 'text', text: trimmed, pairs: [] };
  }

  extractNetworkPaths(...sources: string[]): string[] {
    const found = new Set<string>();
    for (const source of sources) {
      if (!source) {
        continue;
      }
      const matches = source.match(UNC_PATH_REGEX) || [];
      for (const m of matches) {
        found.add(m.replace(/[,;]+$/, ''));
      }
    }
    return Array.from(found);
  }

  extractCaseFileRefs(...sources: string[]): string[] {
    const found = new Set<string>();
    for (const source of sources) {
      if (!source) {
        continue;
      }
      // Only explicit CaseFileRef:[...] — do not treat JSON/XPath [...] as refs
      const caseFileMatch = /CaseFileRef:\s*(\[[^\]]+\])/gi;
      let m: RegExpExecArray | null;
      while ((m = caseFileMatch.exec(source)) !== null) {
        found.add(m[1]);
      }
    }
    return Array.from(found);
  }
}
